import { useCallback, useState } from 'react';
import { v4 as uuidV4 } from 'uuid';
import { useFormContext } from 'react-hook-form';
import { CommonRouteLegSchemaValues } from '@lp/features/RoutePlanner/components/RouteLegForm/validation';
import { useLegFormContext } from '@lp/features/RoutePlanner/components/RouteLegForm/context/LegFormContext';
import { useRouteLocations } from '@lp/features/RoutePlanner/hooks/useRouteLocations';
import { useWatchFields } from '@/hooks/useWatchFields';
import { useAddressSearch } from '@/hooks/useAddressSearch';
import { useCountriesData } from '@/contexts/CountriesDataProvider';
import { LegLocationTypeEnum } from '@/api/apis/route';
import { convertHereLocation, getLocationAddress } from '@/utils/helpers';

type UseCustomAddressProps = {
  type: 'arrivalLocation' | 'departureLocation';
  handleDateTime?: (timezone: string) => void;
};

export const useCustomLocationAddress = ({ type, handleDateTime }: UseCustomAddressProps) => {
  const { list } = useRouteLocations();
  const { calculateLegTimeDuration, setLegDuration } = useLegFormContext();
  const { countriesByIso2, resolveSubdivisionName } = useCountriesData();
  const [initAddressSearch, setInitAddressSearch] = useState(false);
  const { setValue } = useFormContext<CommonRouteLegSchemaValues>();
  const {
    formType,
    legType,
    [type]: locationValue,
  } = useWatchFields<CommonRouteLegSchemaValues>([`${type}`, 'departureDateTime', 'formType', 'legType'] as const);
  const countryValue = locationValue.locationType === LegLocationTypeEnum.custom ? locationValue.country : undefined;

  const { list: addressList } = useAddressSearch(
    { address: locationValue.displayName || '', countryCode: countryValue },
    initAddressSearch && locationValue.locationType === LegLocationTypeEnum.custom,
  );

  const onAddressSelect = useCallback(
    async (addressText: string) => {
      const addressItem = addressList.find((a) => a.id === addressText);

      if (addressItem) {
        setInitAddressSearch(false);

        const location = convertHereLocation(addressItem, countriesByIso2);
        const currentLocationString = getLocationAddress(location, resolveSubdivisionName);
        const logisticsLocation = list.find(
          (l) => l.locationType === LegLocationTypeEnum.custom && l.hereMapsId === addressItem.id,
        );

        setValue(`${type}.id`, logisticsLocation ? logisticsLocation.id : uuidV4(), { shouldValidate: true });
        setValue(`${type}.addressLine`, location.addressLine, {
          shouldValidate: true,
        });
        setValue(`${type}.hereMapsId`, addressItem.id);
        setValue(`${type}.state`, location.state);
        setValue(`${type}.city`, location.city);
        setValue(`${type}.country`, location.country);
        setValue(`${type}.postalCode`, location.postalCode);
        setValue(`${type}.longitude`, location.longitude);
        setValue(`${type}.latitude`, location.latitude);
        setValue(`${type}.timezone`, location.timezone);
        setValue(`${type}.displayName`, currentLocationString);

        const data = await calculateLegTimeDuration(legType);
        setLegDuration(formType, legType, data);

        if (handleDateTime) {
          handleDateTime(location.timezone);
        }
      }
    },
    [
      addressList,
      list,
      setValue,
      type,
      calculateLegTimeDuration,
      formType,
      setLegDuration,
      legType,
      handleDateTime,
      countriesByIso2,
      resolveSubdivisionName,
    ],
  );

  const onAddressInputChange = useCallback(
    (text: string) => {
      setValue(`${type}.id`, '');
      setValue(`${type}.hereMapsId`, '');
      setValue(`${type}.displayName`, text);
      setInitAddressSearch(true);
    },
    [setValue, type],
  );

  return {
    addressList,
    onAddressSelect,
    onAddressInputChange,
  };
};
